/**
 * In-app congrats messages (work anniversaries etc.).
 *
 * Server-backed via the congrats API when VITE_API_BASE_URL is set — the
 * recipient sees messages on any device. Dev/mock fallback keeps a local
 * map so the flow is demoable offline.
 */

const STORAGE_KEY = 'peoplevue_congrats';
const API_BASE = import.meta.env.VITE_API_BASE_URL;

let apiToken = null;

function readLocal() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
}

function writeLocal(map) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}

/**
 * Fetch the signed-in user's received congrats from the server into the
 * local cache. Call once after sign-in. Non-fatal on failure.
 */
export async function syncCongrats(token, userId) {
  if (!API_BASE || !token || !userId) return false;
  apiToken = token;
  try {
    const res = await fetch(`${API_BASE}/congrats`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    });
    if (!res.ok) throw new Error(`Congrats API ${res.status}`);
    const data = await res.json();
    const map = readLocal();
    map[userId] = data?.messages || [];
    writeLocal(map);
    return true;
  } catch (err) {
    console.warn('Failed to sync congrats (non-fatal):', err.message);
    return false;
  }
}

/** The user's received congrats (synchronous read from the cache). */
export function getMyCongrats(userId) {
  if (!userId) return [];
  return readLocal()[userId] || [];
}

/**
 * Send a congrats message to a colleague. Resolves true on success.
 * In dev/mock mode the message lands in the local map (visible if the
 * recipient is this same device's user — enough to demo the flow).
 */
export async function sendCongrats(recipientId, message, { fromId, fromName } = {}) {
  const trimmed = (message || '').trim();
  if (!recipientId || !trimmed) return false;

  if (API_BASE && apiToken) {
    try {
      const res = await fetch(`${API_BASE}/congrats/${recipientId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiToken}` },
        body: JSON.stringify({ message: trimmed }),
      });
      if (!res.ok) throw new Error(`Congrats API ${res.status}`);
      return true;
    } catch (err) {
      console.warn('Failed to send congrats:', err.message);
      return false;
    }
  }

  // Dev fallback — append locally
  const map = readLocal();
  map[recipientId] = [
    ...(map[recipientId] || []),
    {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      fromId: fromId || null,
      fromName: fromName || 'A colleague',
      message: trimmed,
      sentAt: new Date().toISOString(),
      read: false,
    },
  ];
  writeLocal(map);
  return true;
}

/**
 * Mark congrats as read. Pass a messageId to mark just that one.
 *
 * Reading is a flag, never a delete: a colleague's anniversary note stays in the
 * record so it can be re-read. The inbox previously rendered only unread
 * messages and marked ALL of them read from a single X, so one click made every
 * message permanently unreachable.
 *
 * The server API only supports mark-all (PUT /congrats/read), so a single-message
 * read is applied locally and the server is only told once everything is read.
 * That errs toward "still unread on another device", which is the harmless
 * direction — a message shown twice beats a message lost.
 */
export function markCongratsRead(userId, messageId = null) {
  if (!userId) return;
  const map = readLocal();
  const list = map[userId];
  if (!list?.length) return;

  map[userId] = list.map((m) =>
    messageId === null || m.id === messageId ? { ...m, read: true } : m,
  );
  writeLocal(map);

  const allRead = map[userId].every((m) => m.read);
  if (API_BASE && apiToken && allRead) {
    fetch(`${API_BASE}/congrats/read`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${apiToken}` },
    }).catch((err) => console.warn('Failed to mark congrats read:', err.message));
  }
}

/** Unread subset, for the inbox banner. */
export function getUnreadCongrats(userId) {
  return getMyCongrats(userId).filter((m) => !m.read);
}
